import React, { useState, useEffect } from 'react';
import axios from 'axios';

// --- Interfaces ---
interface NurseContact        { id: number; phone_number: string; }
interface NurseWhatsApp       { id: number; whatsapp_number: string; }
interface NurseEmail          { id: number; email: string; }
interface NurseQualification  { id: number; qualification_name: string; institution_name: string; }
interface NurseRegistration   { id: number; council_name: string; register_number: string; }
interface NurseWorkExperience { id: number; institution_name: string; }
interface NurseLanguage       { id: number; language: string; }
interface NurseEmergencyContact { id: number; name: string; contact_relationship: string; phone_number: string; }
interface NurseDocument       { id: number; document_name: string; file_url: string; }

interface NurseData {
  id: number;
  full_name: string;
  photo_url?: string;
  dob: string;
  sex: string;
  father_spouse_name?: string;
  height?: number;
  weight?: number;
  blood_group?: string;
  religion?: string;
  marital_status?: string;
  native_state?: string;
  native_district?: string;
  aadhar_number?: string;
  pan_number?: string;
  passport_number?: string;
  id_name?: string;
  id_designation?: string;
  id_blood_group?: string;
  emp_id?: string;
  date_of_joining?: string;
  designation?: string;
  profile?: string;
  clinical_class?: string;
  total_experience?: string;
  departments?: string;
  staff_status?: string;
  monthly_salary?: number;
  document_status?: string;
  general_information?: string;
  disciplinary_actions?: string;
  employment_end_date?: string;
  character_attitude?: string;
  special_remarks?: string;
  experience_certificate_issued?: boolean;
  exp_father_spouse_name?: string;
  exp_nursing_reg_txt?: string;
  exp_designation?: string;
  exp_department?: string;
  exp_still_working?: string;
  exp_start_date?: string;
  exp_end_date?: string;
  // Bank fields
  bank_account_name?: string;
  bank_account_type?: string;
  bank_account_number?: string;
  bank_name?: string;
  bank_branch?: string;
  bank_state?: string;
  bank_ifsc?: string;
  contacts?: NurseContact[];
  whatsapp_numbers?: NurseWhatsApp[];
  emails?: NurseEmail[];
  qualifications?: NurseQualification[];
  registrations?: NurseRegistration[];
  work_experiences?: NurseWorkExperience[];
  languages?: NurseLanguage[];
  emergency_contacts?: NurseEmergencyContact[];
  documents?: NurseDocument[];
}

interface ViewNurseModalProps { isOpen: boolean; onClose: () => void; nurseId: number | null; }

const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e2e8f0'/%3E%3Ccircle cx='50' cy='38' r='18' fill='%2394a3b8'/%3E%3Cellipse cx='50' cy='80' rx='28' ry='20' fill='%2394a3b8'/%3E%3C/svg%3E";

const getApiBaseUrl = () => {
  let url = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
  if (window.location.protocol === 'https:' && url.startsWith('http:')) url = url.replace('http:', 'https:');
  return url;
};

// Status config
// Same status vocabulary + palette as Staff Pool / Nurse Management — one
// status system, colors kept visually consistent everywhere.
const STATUS_CONFIG: Record<string, { bg: string; color: string; dot: string }> = {
  'Available':                     { bg: '#dcfce7', color: '#15803d', dot: '#16a34a' },
  'On Temporary Duty':              { bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6' },
  'Back-Up':                        { bg: '#fef3c7', color: '#b45309', dot: '#f59e0b' },
  'On Duty - Shuffle Possibility':  { bg: '#e0e7ff', color: '#4338ca', dot: '#6366f1' },
  'On Duty':                        { bg: '#e0f2fe', color: '#0369a1', dot: '#0ea5e9' },
  'On Leave - Regular':             { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'On Leave - Emergency':           { bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' },
  'On Leave - Sick':                { bg: '#ffedd5', color: '#c2410c', dot: '#f97316' },
  'Uncertainty':                    { bg: '#fef9c3', color: '#a16207', dot: '#eab308' },
  'Absconded':                      { bg: '#fee2e2', color: '#7f1d1d', dot: '#dc2626' },
  'Resigned':                       { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' },
};
const getStatusConfig = (status?: string) => STATUS_CONFIG[status || ''] || { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };

const getDocStatusConfig = (status?: string) => {
  const s = status?.toLowerCase() || '';
  if (s.includes('full'))    return { bg: '#dcfce7', color: '#15803d' };
  if (s.includes('not'))     return { bg: '#fee2e2', color: '#b91c1c' };
  return                            { bg: '#f1f5f9', color: '#475569' };
};

const ViewNurseModal: React.FC<ViewNurseModalProps> = ({ isOpen, onClose, nurseId }) => {
  const [nurse,    setNurse]    = useState<NurseData | null>(null);
  const [loading,  setLoading]  = useState(false);
  // Live status — same source Staff Pool reads (nurse_availability for today,
  // with "On Duty" applied on top if assigned today), not the dormant
  // staff_status field.
  const [liveStatus, setLiveStatus] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'professional' | 'hr' | 'contacts' | 'documents'>('overview');

  const getFullUrl = (path?: string) => {
    if (!path) return PLACEHOLDER_IMG;
    return path.startsWith('http') ? path : `${getApiBaseUrl()}/${path}`;
  };

  const formatDate = (d?: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calculateAge = (dob?: string) => {
    if (!dob) return '—';
    const today = new Date(), birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  useEffect(() => {
    if (!isOpen || !nurseId) return;
    setActiveTab('overview');
    (async () => {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      try {
        const r = await axios.get(`${getApiBaseUrl()}/api/nurses/${nurseId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        setNurse(r.data);

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const today = new Date().toISOString().slice(0, 10);
        try {
          const [availRes, asgnRes] = await Promise.all([
            axios.get(`${getApiBaseUrl()}/api/nurse-availability`, { headers, params: { date: today } }),
            axios.get(`${getApiBaseUrl()}/api/assignments`, { headers, params: { start_date: today, end_date: today } }),
          ]);
          const onDuty = (asgnRes.data || []).some((a: any) => a.nurse?.id === Number(nurseId) || a.second_nurse?.id === Number(nurseId));
          const row = (availRes.data || []).find((rr: any) => rr.nurse_id === Number(nurseId));
          setLiveStatus(onDuty ? 'On Duty' : (row?.category || 'Available'));
        } catch (e) { console.error('fetch live status', e); }
      } catch (e) {
        console.error('Failed to fetch nurse details:', e);
      } finally { setLoading(false); }
    })();
  }, [isOpen, nurseId]);

  // ── COPY DETAILS (unchanged logic) ──────────────────────────────────────
  const handleCopyDetails = async () => {
    if (!nurse) return;
    const age = calculateAge(nurse.dob);
    const ageStr = age !== '—' ? `${age} Years` : '';
    const makeBold = (text?: string) => { const t = text?.trim(); return t ? `*${t}*` : ''; };

    const qualifications = nurse.qualifications?.map(q =>
      [makeBold(q.qualification_name), q.institution_name?.trim()].filter(Boolean).join('\n')
    ).join('\n\n') || '';

    const registrations = nurse.registrations?.map(r => {
      const cName = r.council_name?.trim() || '';
      const rNum  = r.register_number?.trim() || '';
      if (rNum) return `${cName} *Register Number - ${rNum}*`.trim();
      return cName;
    }).join('\n') || '';

    const hospitals  = nurse.work_experiences?.map(w => w.institution_name?.trim()).filter(Boolean).join('\n') || '';
    const languages  = nurse.languages?.map(l => l.language?.trim()).filter(Boolean).join(', ') || '';
    const native     = [nurse.native_district?.trim(), nurse.native_state?.trim()].filter(Boolean).join(', ');
    const heightWeight = [nurse.height ? `${nurse.height}cm` : '', nurse.weight ? `${nurse.weight}kg` : ''].filter(Boolean).join(' / ');

    let formattedAadhar = 'Aadhar : ';
    if (nurse.aadhar_number) {
      const clean = nurse.aadhar_number.replace(/\s+/g, '');
      formattedAadhar = clean.length === 12
        ? `Aadhar : *${clean.slice(0, 4)} ${clean.slice(4, 8)}* ${clean.slice(8, 12)}`
        : `Aadhar : ${makeBold(nurse.aadhar_number)}`;
    }

    const textParts = [
      makeBold(nurse.full_name),
      `${ageStr} ${nurse.sex || ''}`.trim(),
      '',
      qualifications,
      makeBold(nurse.designation),
      registrations,
      '',
      `Experience : ${makeBold(nurse.total_experience)}`,
      makeBold(nurse.departments),
      hospitals,
      '',
      '*Languages :*', languages,
      '',
      '*Native :*', native,
      '',
      '*Height & Weight*', heightWeight,
      '',
      '*ID Proofs :*',
      formattedAadhar,
      `Company ID : ${nurse.emp_id?.trim() || ''}`,
      '',
      `Blood Group : ${nurse.id_blood_group?.trim() || nurse.blood_group?.trim() || ''}`,
    ];

    const text = textParts.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      alert('Failed to copy details to clipboard.');
    }
  };

  if (!isOpen) return null;

  const statusCfg    = getStatusConfig(liveStatus);
  const docStatusCfg = getDocStatusConfig(nurse?.document_status);

  // ── TAB CONFIG ───────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',      label: 'Overview',      icon: '👤' },
    { id: 'professional',  label: 'Professional',  icon: '🏥' },
    { id: 'hr',            label: 'HR & Ops',      icon: '📊' },
    { id: 'contacts',      label: 'Contacts',      icon: '📞' },
    { id: 'documents',     label: 'Documents',     icon: '📁' },
  ] as const;

  // ── MINI COMPONENTS ──────────────────────────────────────────────────────
  const Field = ({ label, value, fullWidth }: { label: string; value: React.ReactNode; fullWidth?: boolean }) => (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 500, lineHeight: 1.5 }}>{value || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
    </div>
  );

  const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
    <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem', ...style }}>{children}</div>
  );

  const CardTitle = ({ icon, title }: { icon: string; title: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: '1rem' }}>{icon}</span>
      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
    </div>
  );

  const Grid = ({ cols = 3, children }: { cols?: number; children: React.ReactNode }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1rem' }}>{children}</div>
  );

  const Tag = ({ children, color = '#e2e8f0', textColor = '#475569' }: { children: React.ReactNode; color?: string; textColor?: string }) => (
    <span style={{ backgroundColor: color, color: textColor, padding: '3px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, display: 'inline-block' }}>{children}</span>
  );

  // Bank info — always show the section so it's visible even when fields are partially filled.
  // If your backend uses different field names, map them here.
  const bankData = nurse ? {
    account_name:   nurse.bank_account_name   ?? (nurse as any).bank_name_on_account ?? '',
    account_type:   nurse.bank_account_type   ?? '',
    account_number: nurse.bank_account_number ?? (nurse as any).account_number ?? '',
    bank_name:      nurse.bank_name           ?? (nurse as any).bank ?? '',
    branch:         nurse.bank_branch         ?? (nurse as any).branch_name ?? '',
    ifsc:           nurse.bank_ifsc           ?? (nurse as any).ifsc_code ?? '',
    state:          nurse.bank_state          ?? '',
  } : null;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-modal, #printable-modal * { visibility: visible; }
          #printable-modal { position: absolute; left: 0; top: 0; width: 100%; height: auto; overflow: visible !important; background: white; box-shadow: none !important; border-radius: 0 !important; }
          .no-print { display: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .doc-card:hover { background: #f8fafc !important; }
        .tab-btn:hover  { background: #f1f5f9 !important; }
      `}</style>

      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }} onClick={onClose}>
        <div id="printable-modal" style={{ backgroundColor: '#f8fafc', borderRadius: '16px', width: '96%', maxWidth: '1100px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 64px -12px rgba(0,0,0,0.35)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

          {/* ── TOP BAR ── */}
          <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0.875rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }} className="no-print">
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Nurse Profile</span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={handleCopyDetails} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: isCopied ? '#dcfce7' : '#eff6ff', color: isCopied ? '#166534' : '#1d4ed8', border: `1px solid ${isCopied ? '#bbf7d0' : '#bfdbfe'}`, padding: '0.45rem 1rem', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem', transition: 'all 0.2s' }}>
                {isCopied ? '✅ Copied!' : '📋 Copy Details'}
              </button>
              <button onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '0.45rem 1rem', borderRadius: '7px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                🖨️ Print
              </button>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: '0.2rem 0.4rem', borderRadius: '6px' }}>&times;</button>
            </div>
          </div>

          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '0.95rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                Loading profile...
              </div>
            </div>
          ) : !nurse ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>Could not load nurse data.</div>
          ) : (
            <>
              {/* ── PROFILE HERO ── */}
              <div style={{ backgroundColor: '#fff', padding: '1.5rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                  {/* Avatar */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={nurse.photo_url ? getFullUrl(nurse.photo_url) : PLACEHOLDER_IMG} alt="Profile" onError={e => (e.currentTarget.src = PLACEHOLDER_IMG)}
                      style={{ width: '88px', height: '88px', borderRadius: '12px', objectFit: 'cover', border: '2px solid #e2e8f0', backgroundColor: '#f1f5f9' }} />
                    <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: statusCfg.dot, border: '2px solid white' }} />
                  </div>

                  {/* Name & Badges */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{nurse.full_name}</h2>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {nurse.designation && <Tag color="#dbeafe" textColor="#1d4ed8">{nurse.designation}</Tag>}
                      {liveStatus && <Tag color={statusCfg.bg} textColor={statusCfg.color}><span style={{ marginRight: '4px' }}>●</span>{liveStatus}</Tag>}
                      {nurse.emp_id      && <Tag color="#f1f5f9" textColor="#475569">ID: {nurse.emp_id}</Tag>}
                      {nurse.document_status && <Tag color={docStatusCfg.bg} textColor={docStatusCfg.color}>{nurse.document_status}</Tag>}
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      {nurse.contacts?.map(c    => <span key={c.id}  style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>📞 {c.phone_number}</span>)}
                      {nurse.whatsapp_numbers?.map(w => <span key={w.id} style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 500 }}>📱 {w.whatsapp_number}</span>)}
                      {nurse.emails?.map(e      => <span key={e.id}  style={{ fontSize: '0.82rem', color: '#64748b' }}>✉️ {e.email}</span>)}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div style={{ display: 'flex', gap: '1px', backgroundColor: '#e2e8f0', borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }} className="no-print">
                    {[
                      { label: 'Age',        value: `${calculateAge(nurse.dob)} yrs` },
                      { label: 'Experience', value: nurse.total_experience || '—' },
                      { label: 'Salary',     value: nurse.monthly_salary ? `₹${nurse.monthly_salary.toLocaleString()}` : '—' },
                    ].map(stat => (
                      <div key={stat.label} style={{ backgroundColor: '#fff', padding: '0.75rem 1.25rem', textAlign: 'center', minWidth: '90px' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{stat.value}</div>
                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── TABS ── */}
              <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 1.5rem', display: 'flex', gap: '0', flexShrink: 0 }} className="no-print">
                {TABS.map(tab => (
                  <button key={tab.id} className="tab-btn" onClick={() => setActiveTab(tab.id as any)}
                    style={{ padding: '0.75rem 1rem', fontSize: '0.82rem', fontWeight: 600, border: 'none', borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent', backgroundColor: 'transparent', color: activeTab === tab.id ? '#1d4ed8' : '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.15s', borderRadius: '0', whiteSpace: 'nowrap' }}>
                    <span>{tab.icon}</span>{tab.label}
                  </button>
                ))}
              </div>

              {/* ── TAB CONTENT ── */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {/* ══ OVERVIEW ══ */}
                {activeTab === 'overview' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <Card>
                        <CardTitle icon="🧑" title="Personal Details" />
                        <Grid cols={2}>
                          <Field label="Date of Birth" value={`${formatDate(nurse.dob)} (${calculateAge(nurse.dob)} yrs)`} />
                          <Field label="Gender"        value={nurse.sex} />
                          <Field label="Blood Group"   value={nurse.id_blood_group || nurse.blood_group} />
                          <Field label="Marital"       value={nurse.marital_status} />
                          <Field label="Religion"      value={nurse.religion} />
                          <Field label="Native"        value={[nurse.native_district, nurse.native_state].filter(Boolean).join(', ')} />
                          <Field label="Height"        value={nurse.height ? `${nurse.height} cm` : undefined} />
                          <Field label="Weight"        value={nurse.weight ? `${nurse.weight} kg` : undefined} />
                        </Grid>
                      </Card>

                      <Card>
                        <CardTitle icon="🗣️" title="Languages" />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {nurse.languages?.length
                            ? nurse.languages.map(l => <Tag key={l.id} color="#f0f9ff" textColor="#0369a1">{l.language}</Tag>)
                            : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>None recorded</span>}
                        </div>
                      </Card>
                    </div>

                    <Card>
                      <CardTitle icon="🪪" title="Identity & Company ID" />
                      <Grid cols={3}>
                        <Field label="Aadhar Number"  value={nurse.aadhar_number} />
                        <Field label="PAN Number"     value={nurse.pan_number} />
                        <Field label="Passport No."   value={nurse.passport_number} />
                        <Field label="Name on ID"     value={nurse.id_name} />
                        <Field label="ID Designation" value={nurse.id_designation} />
                        <Field label="Emp ID"         value={nurse.emp_id} />
                        <Field label="Date of Joining" value={formatDate(nurse.date_of_joining)} />
                        <Field label="Blood Group"    value={nurse.id_blood_group} />
                      </Grid>
                    </Card>
                  </>
                )}

                {/* ══ PROFESSIONAL ══ */}
                {activeTab === 'professional' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <Card>
                        <CardTitle icon="🎓" title="Education" />
                        {nurse.qualifications?.length ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {nurse.qualifications.map(q => (
                              <div key={q.id} style={{ padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #3b82f6' }}>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{q.qualification_name}</div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>{q.institution_name}</div>
                              </div>
                            ))}
                          </div>
                        ) : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>No qualifications</span>}
                      </Card>

                      <Card>
                        <CardTitle icon="📋" title="Council Registrations" />
                        {nurse.registrations?.length ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {nurse.registrations.map(r => (
                              <div key={r.id} style={{ padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #8b5cf6' }}>
                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{r.council_name}</div>
                                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Reg: {r.register_number}</div>
                              </div>
                            ))}
                          </div>
                        ) : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>Not registered</span>}
                      </Card>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <Card>
                        <CardTitle icon="🏥" title="Work History" />
                        {nurse.work_experiences?.length ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {nurse.work_experiences.map((w, i) => (
                              <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                                <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
                                <span style={{ fontSize: '0.88rem', color: '#334155', fontWeight: 500 }}>{w.institution_name}</span>
                              </div>
                            ))}
                          </div>
                        ) : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>None recorded</span>}
                      </Card>

                      <Card>
                        <CardTitle icon="🏢" title="Professional Profile" />
                        <Grid cols={1}>
                          <Field label="Grade Level (Profile)" value={nurse.profile} />
                          <Field label="Profile Level (Class)" value={nurse.clinical_class} />
                          <Field label="Total Experience"      value={nurse.total_experience} />
                          <Field label="Departments Worked"    value={nurse.departments} />
                        </Grid>
                      </Card>
                    </div>

                    {/* Exp Certificate */}
                    {nurse.experience_certificate_issued && (
                      <Card style={{ borderColor: '#fde68a', backgroundColor: '#fffbeb' }}>
                        <CardTitle icon="📜" title="Experience Certificate Details" />
                        <Grid cols={3}>
                          <Field label="Father / Spouse" value={nurse.exp_father_spouse_name} />
                          <Field label="Nursing Reg"     value={nurse.exp_nursing_reg_txt} />
                          <Field label="Designation"     value={nurse.exp_designation} />
                          <Field label="Department"      value={nurse.exp_department} />
                          <Field label="Start Date"      value={formatDate(nurse.exp_start_date)} />
                          <Field label="End Date"        value={formatDate(nurse.exp_end_date)} />
                          <Field label="Still Working"   value={nurse.exp_still_working} />
                        </Grid>
                      </Card>
                    )}
                  </>
                )}

                {/* ══ HR & OPS ══ */}
                {activeTab === 'hr' && (
                  <>
                    <Card>
                      <CardTitle icon="📊" title="Operational Data" />
                      <Grid cols={4}>
                        <Field label="Monthly Salary"    value={nurse.monthly_salary ? `₹${nurse.monthly_salary.toLocaleString()}` : undefined} />
                        <Field label="Staff Status"      value={liveStatus} />
                        <Field label="Document Status"   value={nurse.document_status} />
                        <Field label="Employment End"    value={formatDate(nurse.employment_end_date)} />
                      </Grid>
                    </Card>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <Card>
                        <CardTitle icon="📝" title="General Information" />
                        <div style={{ fontSize: '0.875rem', color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: '60px' }}>
                          {nurse.general_information || <span style={{ color: '#cbd5e1' }}>No general information recorded.</span>}
                        </div>
                      </Card>
                      <Card style={{ borderColor: '#fecaca' }}>
                        <CardTitle icon="⚠️" title="Disciplinary Actions" />
                        <div style={{ fontSize: '0.875rem', color: '#b91c1c', lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: '60px' }}>
                          {nurse.disciplinary_actions || <span style={{ color: '#cbd5e1', fontWeight: 'normal' }}>No disciplinary actions recorded.</span>}
                        </div>
                      </Card>
                    </div>

                    {/* Special Remarks */}
                    {(nurse.character_attitude || nurse.special_remarks) && (
                      <Card>
                        <CardTitle icon="💬" title="Remarks" />
                        <Grid cols={2}>
                          <Field label="Character & Attitude" value={nurse.character_attitude} />
                          <Field label="Special Remarks"      value={nurse.special_remarks} />
                        </Grid>
                      </Card>
                    )}

                    {/* Bank Account — always shown */}
                    <Card style={{ borderColor: '#e0f2fe' }}>
                      <CardTitle icon="🏦" title="Bank Account Details" />
                      {bankData && (bankData.account_number || bankData.bank_name || bankData.ifsc || bankData.account_name) ? (
                        <Grid cols={3}>
                          <Field label="Account Name"   value={bankData.account_name} />
                          <Field label="Account Type"   value={bankData.account_type} />
                          <Field label="Account Number" value={
                            bankData.account_number
                              ? <span style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                                  <span style={{ color: '#94a3b8' }}>•••• •••• </span>
                                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{bankData.account_number.slice(-4)}</span>
                                </span>
                              : undefined
                          } />
                          <Field label="Bank Name"  value={bankData.bank_name} />
                          <Field label="Branch"     value={bankData.branch} />
                          <Field label="IFSC Code"  value={
                            bankData.ifsc
                              ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0369a1', letterSpacing: '0.05em' }}>{bankData.ifsc}</span>
                              : undefined
                          } />
                          <Field label="State"      value={bankData.state} />
                        </Grid>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', color: '#0369a1', fontSize: '0.85rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                          No bank account details have been added yet. Edit the nurse profile to add bank information.
                        </div>
                      )}
                    </Card>
                  </>
                )}

                {/* ══ CONTACTS ══ */}
                {activeTab === 'contacts' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                      <Card>
                        <CardTitle icon="📞" title="Mobile Numbers" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {nurse.contacts?.length
                            ? nurse.contacts.map(c => <div key={c.id} style={{ padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>{c.phone_number}</div>)
                            : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>—</span>}
                        </div>
                      </Card>
                      <Card>
                        <CardTitle icon="📱" title="WhatsApp Numbers" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {nurse.whatsapp_numbers?.length
                            ? nurse.whatsapp_numbers.map(w => <div key={w.id} style={{ padding: '8px 10px', backgroundColor: '#f0fdf4', borderRadius: '6px', fontSize: '0.88rem', fontWeight: 600, color: '#15803d' }}>{w.whatsapp_number}</div>)
                            : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>—</span>}
                        </div>
                      </Card>
                      <Card>
                        <CardTitle icon="✉️" title="Email Addresses" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {nurse.emails?.length
                            ? nurse.emails.map(e => <div key={e.id} style={{ padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem', color: '#475569' }}>{e.email}</div>)
                            : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>—</span>}
                        </div>
                      </Card>
                    </div>

                    <Card>
                      <CardTitle icon="🆘" title="Emergency Contacts" />
                      {nurse.emergency_contacts?.length ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {nurse.emergency_contacts.map(c => (
                            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', padding: '10px 14px', backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: '3px solid #fca5a5', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Name</div>
                                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>{c.name}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Relationship</div>
                                <div style={{ fontSize: '0.85rem', color: '#475569' }}>{c.contact_relationship}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Phone</div>
                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.88rem' }}>📞 {c.phone_number}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <span style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>No emergency contacts listed.</span>}
                    </Card>
                  </>
                )}

                {/* ══ DOCUMENTS ══ */}
                {activeTab === 'documents' && (
                  <Card>
                    <CardTitle icon="📁" title="Uploaded Documents" />
                    {nurse.documents?.length ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                        {nurse.documents.map(doc => (
                          <a key={doc.id} href={getFullUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" className="doc-card"
                            style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', textDecoration: 'none', color: '#334155', backgroundColor: '#fff', transition: 'background 0.15s', cursor: 'pointer' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', marginBottom: '0.6rem' }}>📄</div>
                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', whiteSpace: 'nowrap' }} title={doc.document_name}>{doc.document_name}</div>
                            <span style={{ fontSize: '0.72rem', color: '#3b82f6', marginTop: '6px', fontWeight: 600 }}>View →</span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
                        No documents uploaded.
                      </div>
                    )}
                  </Card>
                )}

              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewNurseModal;